import { computed, type ComputedRef, type Ref } from 'vue';

/** Minimal stem shape needed to group stems by system and type. */
export interface GroupableStem {
    id: string;
    type: string;
    system?: string;
}

export interface StemTypeGroup {
    type: string;
    ids: string[];
}

export interface StemSystemGroup {
    system: string;
    systemLabel: string;
    typeGroups: StemTypeGroup[];
}

export interface UseStemSystemGroupingOptions {
    availableStems: ComputedRef<GroupableStem[]>;
    selectedStemIds: Ref<string[]>;
    stemSystems: ComputedRef<Array<{ key: string; label: string }>>;
}

export interface UseStemSystemGroupingReturn {
    stemSystemGroups: ComputedRef<StemSystemGroup[]>;
    selectedStemTypesBySystem: ComputedRef<Map<string, string[]>>;
    updateSelectedStems: (system: string, selectedTypes: string[]) => void;
}

function systemKeyOf(stem: GroupableStem): string {
    if (stem.system) {
        return stem.system;
    }
    const [first, second] = stem.id.split('-');
    return `${first}-${second}`;
}

/** Group stems by system → type, ordered by the supplied stemSystems list. */
function buildStemSystemGroups(
    stems: GroupableStem[],
    stemSystems: Array<{ key: string; label: string }>,
): StemSystemGroup[] {
    const systemMap = new Map<string, Map<string, StemTypeGroup>>();

    stems.forEach((stem) => {
        const system = systemKeyOf(stem);
        const typeMap = systemMap.get(system) ?? new Map<string, StemTypeGroup>();
        const typeGroup = typeMap.get(stem.type) ?? { type: stem.type, ids: [] };
        typeGroup.ids.push(stem.id);
        typeMap.set(stem.type, typeGroup);
        systemMap.set(system, typeMap);
    });

    return stemSystems.flatMap(({ key, label }) => {
        const typeMap = systemMap.get(key);
        return typeMap
            ? [{ system: key, systemLabel: label, typeGroups: Array.from(typeMap.values()) }]
            : [];
    });
}

/** The selected stem types per system, derived from the selected stem ids. */
function selectedTypesBySystem(
    groups: StemSystemGroup[],
    selectedIds: string[],
): Map<string, string[]> {
    const result = new Map<string, string[]>();
    groups.forEach((group) => {
        const selectedTypes = group.typeGroups
            .filter((typeGroup) => typeGroup.ids.some((id) => selectedIds.includes(id)))
            .map((typeGroup) => typeGroup.type);
        if (selectedTypes.length > 0) {
            result.set(group.system, selectedTypes);
        }
    });
    return result;
}

/** Flatten a system→types selection back into the set of selected stem ids. */
function stemIdsForSelection(
    groups: StemSystemGroup[],
    selectionsBySystem: Map<string, string[]>,
): string[] {
    return groups.flatMap((group) => {
        const selectedTypes = selectionsBySystem.get(group.system);
        if (!selectedTypes) {
            return [];
        }
        return group.typeGroups
            .filter((typeGroup) => selectedTypes.includes(typeGroup.type))
            .flatMap((typeGroup) => typeGroup.ids);
    });
}

/**
 * Composable for grouping stems by system and type, managing selection with radio buttons for systems.
 */
export function useStemSystemGrouping(
    options: UseStemSystemGroupingOptions,
): UseStemSystemGroupingReturn {
    const { availableStems, selectedStemIds, stemSystems } = options;

    const stemSystemGroups = computed(() =>
        buildStemSystemGroups(availableStems.value, stemSystems.value),
    );

    const selectedStemTypesBySystem = computed(() =>
        selectedTypesBySystem(stemSystemGroups.value, selectedStemIds.value),
    );

    const updateSelectedStems = (system: string, selectedTypes: string[]) => {
        const selectionsBySystem = new Map(selectedStemTypesBySystem.value);
        if (selectedTypes.length > 0) {
            selectionsBySystem.set(system, selectedTypes);
        } else {
            selectionsBySystem.delete(system);
        }
        selectedStemIds.value = stemIdsForSelection(stemSystemGroups.value, selectionsBySystem);
    };

    return {
        stemSystemGroups,
        selectedStemTypesBySystem,
        updateSelectedStems,
    };
}
